
import React from 'react';
import { RefreshCw } from 'lucide-react';
import { ChunkStatus } from '../../client-merge';

interface ChunkGridProps {
    chunks: ChunkStatus[];
    onRetryChunk?: (chunkIndex: number) => void;
}

export const ChunkGrid: React.FC<ChunkGridProps> = ({ chunks, onRetryChunk }) => {
    if (!chunks || chunks.length === 0) return null;

    const downloadedCount = chunks.filter(c => c.localStatus === 'downloaded').length;
    const totalSize = chunks.reduce((s, c) => s + (c.size || 0), 0);
    const sizeMB = (totalSize / 1024 / 1024).toFixed(1);

    return (
        <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                    Segments ({downloadedCount}/{chunks.length})
                </span>
                <span className="text-[10px] text-gray-400">
                    {sizeMB} MB
                </span>
            </div>
            <div className="grid grid-cols-6 gap-1">
                {chunks.map((chunk) => {
                    // Determine color based on status
                    let bg = 'bg-gray-200'; // Default pending
                    const isLocal = (chunk as any).mode === 'local';
                    const isServer = (chunk as any).mode === 'server';

                    if (chunk.localStatus === 'downloaded') bg = 'bg-green-500';
                    else if (chunk.localStatus === 'failed') bg = 'bg-red-500';
                    else if (chunk.localStatus === 'downloading') bg = isLocal ? 'bg-purple-400' : 'bg-blue-400';
                    else if (chunk.serverStatus === 'complete') bg = 'bg-cyan-400'; // Ready on server, waiting DL
                    else if (chunk.serverStatus === 'encoding') bg = isLocal ? 'bg-purple-500' : 'bg-blue-500';
                    else if (chunk.serverStatus === 'processing') bg = isLocal ? 'bg-purple-500' : 'bg-blue-500';
                    else if (isLocal) bg = 'bg-purple-200';
                    else if (isServer) bg = 'bg-blue-200';

                    // Border color based on mode
                    const border = isLocal ? 'ring-2 ring-purple-400' : isServer ? 'ring-2 ring-blue-400' : '';

                    // Determine progress percentage to show
                    const pct =
                        chunk.localStatus === 'downloading' ? chunk.localProgress :
                            chunk.serverStatus === 'encoding' || chunk.serverStatus === 'processing' ? chunk.serverProgress : 0;

                    // Mode label for pending chunks
                    const modeLabel = isLocal ? '⚡' : isServer ? '☁' : '';

                    return (
                        <div
                            key={chunk.index}
                            onClick={() => {
                                if (chunk.localStatus === 'failed' && onRetryChunk) {
                                    onRetryChunk(chunk.index);
                                }
                            }}
                            className={`relative h-6 rounded ${bg} ${border} overflow-hidden transition-colors duration-300 ${chunk.localStatus === 'failed' && onRetryChunk ? 'cursor-pointer hover:opacity-80 active:scale-95 group' : 'cursor-default'}`}
                            title={`Chunk ${chunk.index + 1}: ${isLocal ? 'Web' : 'Cloud'} | Server=${chunk.serverStatus}, Local=${chunk.localStatus}${chunk.localStatus === 'failed' ? ' (Cliquez pour relancer)' : ''}`}
                        >
                            {/* Progress bar overlay */}
                            {pct > 0 && pct < 100 && (
                                <div
                                    className="absolute inset-y-0 left-0 bg-white/30 transition-all duration-300"
                                    style={{ width: `${pct}%` }}
                                />
                            )}

                            {/* Status Icon/Text */}
                            <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-white drop-shadow-sm select-none">
                                {chunk.localStatus === 'downloaded' ? '✓' :
                                    chunk.localStatus === 'failed' ? '!' :
                                        chunk.localStatus === 'downloading' ? `${pct}%` :
                                            (chunk.serverStatus === 'encoding' || chunk.serverStatus === 'processing') ? `${chunk.serverProgress}%` :
                                                modeLabel || (chunk.index + 1)}
                            </span>

                            {/* Retry Action (Visual overlay on hover) */}
                            {chunk.localStatus === 'failed' && onRetryChunk && (
                                <div className="absolute inset-0 flex items-center justify-center bg-red-600/90 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <RefreshCw className="w-3 h-3 text-white" />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};
